import React, { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faExclamationTriangle,
  faCheckCircle,
  faInfoCircle,
  faClipboardCheck,
} from "@fortawesome/free-solid-svg-icons";
import RawFallback from "../../Shared/RawFallback/RawFallback";
import styles from "./CSVDataQuality.module.css";

const MAX_LEN = 120;
const truncate = (s) => (s.length > MAX_LEN ? s.slice(0, MAX_LEN).trim() + "…" : s);

const dedupe = (items) => {
  const seen = new Set();
  return items.filter((item) => {
    const norm = item.toLowerCase().slice(0, 50).replace(/\s+/g, " ");
    if (seen.has(norm)) return false;
    seen.add(norm);
    return true;
  });
};

const getBullets = (content) => {
  if (!content || !Array.isArray(content)) return [];
  return content
    .map((c) => (c.type === "bullet" ? c.text : c.type === "keyValue" ? `${c.key}: ${c.value}` : c.text))
    .filter(Boolean)
    .map((t) => String(t).trim())
    .filter((t) => t.length > 5);
};

const isEmpty = (v) => v === "" || v == null || String(v).trim() === "";

const computeMissingFromData = (rows, columns) => {
  if (!rows?.length || !columns?.length) return [];
  return columns.map((col, i) => {
    const vals = rows.map((r) => (Array.isArray(r) ? r[i] : r[col]));
    const missing = vals.filter((v) => isEmpty(v)).length;
    return { column: col, missing, total: rows.length, percentage: rows.length ? ((missing / rows.length) * 100).toFixed(1) : "0" };
  });
};

const CSVDataQuality = ({ data, rawText, analysisData }) => {
  if (!data) {
    return (
      <div className={styles.container}>
        <div className={styles.emptyState}>
          <p>No data quality information available</p>
          {rawText && <RawFallback rawText={rawText} accentColor="#22c55e" />}
        </div>
      </div>
    );
  }

  const computedMissing = computeMissingFromData(analysisData?.data || [], analysisData?.columns || []);
  const allComplete = computedMissing.length > 0 && computedMissing.every((r) => r.missing === 0);
  const rowCount = analysisData?.data?.length || 0;

  const sections = [];
  const sectionOrder = ["Data Completeness", "Consistency & Format", "Issues", "Recommendations"];
  const legacyOrder = ["Data Consistency", "Data Accuracy", "Data Validity"];

  if (data.sections) {
    const order = [...sectionOrder, ...legacyOrder];
    const seen = new Set();
    for (const name of order) {
      const sec = data.sections.find((s) => {
        const n = (s.name || "").toLowerCase();
        const target = name.toLowerCase();
        return n.includes(target) || target.includes(n);
      });
      if (sec && sec.content?.length && !seen.has(sec.name)) {
        seen.add(sec.name);
        const bullets = dedupe(getBullets(sec.content));
        if (bullets.length > 0) sections.push({ name: sec.name, bullets });
      }
    }
    data.sections.forEach((sec) => {
      if (seen.has(sec.name) || !sec.content?.length) return;
      const bullets = dedupe(getBullets(sec.content));
      if (bullets.length > 0) sections.push({ name: sec.name, bullets });
    });
  }

  let fallbackBullets = [];
  if (sections.length === 0 && rawText) {
    const parts = rawText.split(/SECTION:\s*/i).filter(Boolean);
    parts.forEach((p) => {
      const bullets = (p.match(/- ([^\n]+)/g) || []).map((b) => b.replace(/^- /, "").trim()).filter((t) => t.length > 10);
      fallbackBullets.push(...bullets);
    });
    fallbackBullets = dedupe(fallbackBullets).slice(0, 10);
  }

  const completenessSec = sections.find((s) => /completeness/i.test(s.name));
  const consistencySec = sections.find((s) => /consistency|format/i.test(s.name));
  const accSec = sections.find((s) => /accuracy/i.test(s.name));
  const valSec = sections.find((s) => /validity/i.test(s.name));
  const consistencyBullets = consistencySec?.bullets
    || dedupe([...(accSec?.bullets || []), ...(valSec?.bullets || [])]);
  const issuesSec = sections.find((s) => /issues/i.test(s.name));
  const recsSec = sections.find((s) => /recommendation/i.test(s.name));

  const hasAnyData =
    allComplete ||
    completenessSec?.bullets?.length ||
    (consistencySec?.bullets?.length || consistencyBullets?.length) ||
    issuesSec?.bullets?.length ||
    recsSec?.bullets?.length ||
    fallbackBullets.length > 0;

  if (!hasAnyData) {
    return (
      <div className={styles.container}>
        <div className={styles.header}>
          <div className={styles.headerIcon}>
            <FontAwesomeIcon icon={faClipboardCheck} />
          </div>
          <div>
            <h2 className={styles.title}>Data Quality</h2>
            <p className={styles.subtitle}>Missing values and inconsistencies</p>
          </div>
        </div>
        <div className={styles.emptyState}>
          <p>No data quality information available</p>
          {rawText && <RawFallback rawText={rawText} accentColor="#22c55e" />}
        </div>
      </div>
    );
  }

  const consistencyItems = consistencyBullets || [];

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.headerIcon}>
          <FontAwesomeIcon icon={faClipboardCheck} />
        </div>
        <div>
          <h2 className={styles.title}>Data Quality</h2>
          <p className={styles.subtitle}>Missing values and inconsistencies</p>
        </div>
      </div>

      {allComplete && computedMissing.length > 0 && (
        <div className={styles.completenessCard}>
          <div className={styles.completenessHeader}>
            <div className={styles.completenessIconWrap}>
              <FontAwesomeIcon icon={faCheckCircle} className={styles.completenessIcon} />
            </div>
            <div className={styles.completenessTitleRow}>
              <h3 className={styles.completenessTitle}>Data Completeness</h3>
              <span className={styles.completenessBadge}>100%</span>
            </div>
          </div>
          <div className={styles.completenessProgress}>
            <div className={styles.completenessBar} style={{ width: "100%" }} />
          </div>
          <div className={styles.completenessStats}>
            <span className={styles.completenessStat}>
              <strong>{computedMissing.length}</strong> columns
            </span>
            <span className={styles.completenessStat}>
              <strong>{rowCount.toLocaleString()}</strong> rows
            </span>
            <span className={styles.completenessStat}>
              <strong>0</strong> missing
            </span>
          </div>
          <p className={styles.completenessMessage}>All columns have complete data. No missing values detected.</p>
        </div>
      )}

      <div className={styles.qualityGrid}>
        {consistencyItems.length > 0 && (
          <QualityCard title="Consistency & Format" icon={faCheckCircle} bullets={dedupe(consistencyItems)} />
        )}
        {issuesSec?.bullets?.length > 0 && (
          <QualityCard title="Issues" icon={faExclamationTriangle} bullets={dedupe(issuesSec.bullets)} />
        )}
        {recsSec?.bullets?.length > 0 && (
          <QualityCard title="Recommendations" icon={faInfoCircle} bullets={dedupe(recsSec.bullets).slice(0, 3)} />
        )}
        {!allComplete && computedMissing.length > 0 && computedMissing.some((r) => r.missing > 0) && (
          <MissingTable rows={computedMissing.filter((r) => r.missing > 0)} />
        )}
      </div>

      {(completenessSec?.bullets?.length > 0 && !allComplete) || fallbackBullets.length > 0 ? (
        <div className={styles.qualityGrid}>
          {completenessSec?.bullets?.length > 0 && !allComplete && (
            <QualityCard title="Data Completeness" icon={faCheckCircle} bullets={completenessSec.bullets} />
          )}
          {fallbackBullets.length > 0 && sections.length === 0 && (
            <QualityCard title="Quality Notes" icon={faInfoCircle} bullets={fallbackBullets} />
          )}
        </div>
      ) : null}
    </div>
  );
};

const QualityCard = ({ title, icon, bullets }) => {
  const [expanded, setExpanded] = useState(false);
  const maxInitial = 4;
  const showAll = expanded || bullets.length <= maxInitial;
  const visible = showAll ? bullets : bullets.slice(0, maxInitial);
  const canExpand = bullets.length > maxInitial && !expanded;

  return (
    <div className={styles.qualityCard}>
      <div className={styles.cardHeader}>
        <FontAwesomeIcon icon={icon} className={styles.cardIcon} />
        <h3 className={styles.cardTitle}>{title}</h3>
      </div>
      <ul className={styles.bulletList}>
        {visible.map((item, idx) => (
          <li key={idx} className={styles.bulletItem}>
            <span className={styles.bulletPoint}>•</span>
            <span>{truncate(item)}</span>
          </li>
        ))}
      </ul>
      {canExpand && (
        <button type="button" className={styles.showMore} onClick={() => setExpanded(true)}>
          Show {bullets.length - maxInitial} more
        </button>
      )}
    </div>
  );
};

const MissingTable = ({ rows }) => {
  if (!rows || rows.length === 0) return null;
  return (
    <div className={styles.missingCard}>
      <div className={styles.cardHeader}>
        <FontAwesomeIcon icon={faExclamationTriangle} className={styles.cardIcon} style={{ color: "#ef4444" }} />
        <h3 className={styles.cardTitle}>Missing Values</h3>
      </div>
      <div className={styles.missingTable}>
        <table>
          <thead>
            <tr>
              <th>Column</th>
              <th>Missing</th>
              <th>%</th>
            </tr>
          </thead>
          <tbody>
            {rows.slice(0, 10).map((r, idx) => (
              <tr key={idx}>
                <td>{r.column}</td>
                <td>{r.missing}</td>
                <td>{r.percentage}%</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default CSVDataQuality;
